/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { Span, TraceState } from "@opentelemetry/api";
import { ReadableSpan } from "@opentelemetry/sdk-trace-base";

import { SpanMetaData } from "./SpanMetaData";
import { Fw4Tag } from "../propagator/Fw4Tag";
import { Attributes } from "../utils/Compat";

// ============================================================================


const cDtMetaDataKey = Symbol("DtMetaDataKey");
const cDtPropagatedResourceAttributesKey = Symbol("DtPropagatedResourceAttributes");

interface DtSpan extends Span {
	[cDtMetaDataKey]?: SpanMetaData;
	[cDtPropagatedResourceAttributesKey]?: Attributes;
}

export function getMetaData(span: Span | ReadableSpan): SpanMetaData | undefined {
	return (span as DtSpan)[cDtMetaDataKey];
}

export function setMetaData(span: DtSpan, metaData: SpanMetaData): void {
	span[cDtMetaDataKey] = metaData;
}

export function getPropagatedResourceAttributes(span: Span | ReadableSpan | undefined): Attributes | undefined {
	if (span == null) {
		return undefined;
	}
	return (span as DtSpan)[cDtPropagatedResourceAttributesKey];
}

export function setPropagatedResourceAttributes(span: Span, attributes: Attributes | undefined): void {
	if (span.isRecording() && attributes != null) {
		(span as DtSpan)[cDtPropagatedResourceAttributesKey] = attributes;
	}
}

// ----------------------------------------------------------------------------
const cDtTagKey = Symbol("DtTagKey");

interface DtTraceState extends TraceState {
	[cDtTagKey]?: Fw4Tag;
}

export function setFw4Tag(traceState: DtTraceState, tag: Fw4Tag): void {
	traceState[cDtTagKey] = tag;
}

export function getFw4Tag(traceState: DtTraceState | undefined): Fw4Tag | undefined {
	if (traceState != null) {
		return traceState[cDtTagKey];
	}
	return undefined;
}
